import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import Accordion from "@/components/Accordion";
import {
  H4,
  HeaderTextBlock,
  P,
  TypographyContainer,
} from "@/components/Typography";
import { useTracking } from "@/requests/hooks/app";
import { tv } from "tailwind-variants";
import { Button } from "@/components/Button";

interface IProps {
  complianceReportGet: IGetComplianceReport;
  complianceReportSet: ISetComplianceReport;
}

const styles = tv({
  slots: {
    complianceReportTextStyles: "compliance-report-text-styles",
    reviewBar:
      "mt-30 flex w-full flex-grow flex-row items-center justify-between rounded-sm bg-primary-light p-15 md:px-25 md:py-20",
  },
});

const ComplianceReportView = ({
  complianceReportGet,
  complianceReportSet,
}: IProps) => {
  const { data: complianceReport, refetch: refetchGetComplianceReport } =
    complianceReportGet;
  const {
    isPending: isPendingSetComplianceReport,
    mutateAsync: setComplianceReport,
  } = complianceReportSet;

  const { id } = useParams();
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();

  const { complianceReportTextStyles, reviewBar } = styles();

  useEffect(() => {
    if (complianceReport) {
      sendTracking({
        eventName: "control-panel.email.compliance-report.view",
        additionalProperties: {
          compliance_report_type: complianceReport.reportType,
          compliance_report_status: complianceReport.status,
        },
      });
    }
  }, [complianceReport]);

  const handleOnClick = async () => {
    if (id) {
      await setComplianceReport(id);
      refetchGetComplianceReport();
    }
  };

  const renderHeaderTextBlock = () => (
    <HeaderTextBlock
      align="left"
      featureTextColor="primary"
      featureText={t("common.complianceReport.header.feature")}
      headingText={t("common.complianceReport.header.heading")}
      headingTextColor="secondary"
    />
  );

  const renderInformationAccordion = () => (
    <Accordion
      topContent={
        <H4 className="font-semibold">
          {t("common.complianceReport.content.info.heading")}
        </H4>
      }
      defaultIsExpanded={true}>
      <div className={complianceReportTextStyles()}>
        <P>{t("common.complianceReport.content.info.paragraph1")}</P>

        <P>{t("common.complianceReport.content.info.paragraph2")}</P>

        <ul>
          <li>
            <Trans i18nKey={"common.complianceReport.content.info.list1"}>
              <b>zero</b>
              one
            </Trans>
          </li>
          <li>
            <Trans i18nKey={"common.complianceReport.content.info.list2"}>
              <b>zero</b>
              one
            </Trans>
          </li>
          <li>
            <Trans i18nKey={"common.complianceReport.content.info.list3"}>
              <b>zero</b>
              one
            </Trans>
          </li>
        </ul>

        <P>{t("common.complianceReport.content.info.paragraph3")}</P>

        <P className="font-bold">
          {t("common.complianceReport.content.info.paragraph4")}
        </P>

        <P>
          <Trans i18nKey="common.complianceReport.content.info.paragraph5">
            <b>zero</b>
            one
            <b>two</b>
            three
            <b>four</b>
          </Trans>
        </P>
      </div>
    </Accordion>
  );

  const renderPartOneAccordion = () => (
    <div className="mt-30">
      <Accordion
        topContent={
          <H4 className="font-semibold">
            {t("common.complianceReport.content.part1.heading")}
          </H4>
        }>
        <div className={complianceReportTextStyles()}>
          <P className="italic">
            {t("common.complianceReport.content.part1.italics")}
          </P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question1")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer1")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question2")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer2")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question3")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer3")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question4")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer4")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question5")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer5")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question6")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer6")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question7")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer71")}</P>
          <P>{t("common.complianceReport.content.part1.answer72")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question8")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer8")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question9")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer9")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part1.question10")}
          </P>
          <P>{t("common.complianceReport.content.part1.answer10")}</P>
        </div>
      </Accordion>
    </div>
  );

  const renderPartTwoAccordion = () => (
    <div className="mt-30">
      <Accordion
        topContent={
          <H4 className="font-semibold">
            {t("common.complianceReport.content.part2.heading")}
          </H4>
        }>
        <div className={complianceReportTextStyles()}>
          <P className="italic">
            {t("common.complianceReport.content.part2.italics")}
          </P>

          <P className="font-bold">
            {t("common.complianceReport.content.part2.question1")}
          </P>
          <P>{t("common.complianceReport.content.part2.answer1")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part2.question2")}
          </P>
          <P>{t("common.complianceReport.content.part2.answer2")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part2.question3")}
          </P>
          <P>{t("common.complianceReport.content.part2.answer3")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part2.question4")}
          </P>
          <P>{t("common.complianceReport.content.part2.answer4")}</P>
        </div>
      </Accordion>
    </div>
  );

  const renderPartThreeAccordion = () => (
    <div className="mt-30">
      <Accordion
        topContent={
          <H4 className="font-semibold">
            {t("common.complianceReport.content.part3.heading")}
          </H4>
        }>
        <div className={complianceReportTextStyles()}>
          <P>
            <i>{t("common.complianceReport.content.part3.italics")}</i>
          </P>

          <P className="font-bold underline">
            {t("common.complianceReport.content.part3.sub_heading1")}
          </P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question1")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer1")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question2")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer2")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question3")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer3")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question4")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer4")}</P>

          <div className="mt-20">
            <P className="font-bold underline">
              {t("common.complianceReport.content.part3.sub_heading2")}
            </P>
          </div>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question5")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer5")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question6")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer6")}</P>

          <div className="mt-20">
            <P className="font-bold underline">
              {t("common.complianceReport.content.part3.sub_heading3")}
            </P>
          </div>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question7")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer7")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question8")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer8")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question9")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer9")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question10")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer10")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question11")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer11")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question12")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer12")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.part3.question13")}
          </P>
          <P>{t("common.complianceReport.content.part3.answer13")}</P>
        </div>
      </Accordion>
    </div>
  );

  const renderDecisionAccordion = () => (
    <div className="mt-30">
      <Accordion
        defaultIsExpanded={true}
        topContent={
          <H4 className="font-semibold">
            {t("common.complianceReport.content.decision.heading")}
          </H4>
        }>
        <div className={complianceReportTextStyles()}>
          <P className="italic">
            {t("common.complianceReport.content.decision.italics")}
          </P>

          <P className="font-bold">
            {t("common.complianceReport.content.decision.question1")}
          </P>
          <P>{t("common.complianceReport.content.decision.answer1")}</P>

          <P className="font-bold">
            {t("common.complianceReport.content.decision.question2")}
          </P>
          <P>{t("common.complianceReport.content.decision.answer2")}</P>

          {!!complianceReport?.reviewedAt && !!complianceReport?.reviewedBy && (
            <>
              <P>
                <Trans
                  i18nKey={
                    "common.complianceReport.content.decision.completed_by"
                  }
                  values={{
                    email: complianceReport?.reviewedBy || "-",
                  }}>
                  <b>zero</b>
                  one
                </Trans>
              </P>
              <P>
                <Trans
                  i18nKey={
                    "common.complianceReport.content.decision.completed_date"
                  }
                  values={{
                    date: complianceReport?.reviewedAt || "-",
                  }}>
                  <b>zero</b>
                  one
                </Trans>
              </P>
            </>
          )}
        </div>
      </Accordion>
    </div>
  );

  const renderContent = () => (
    <div className="mt-30">
      {renderInformationAccordion()}
      {renderPartOneAccordion()}
      {renderPartTwoAccordion()}
      {renderPartThreeAccordion()}
      {renderDecisionAccordion()}
    </div>
  );

  const renderReviewBar = () => {
    if (complianceReport?.status !== "DRAFT") return null;

    return (
      <div className={reviewBar()}>
        {t("common.complianceReport.bottomBar.label")}

        <div>
          <Button
            as="button"
            trackingId={{
              eventName: "control-panel.email.compliance-report.review",
              additionalProperties: {
                compliance_report_type: complianceReport.reportType,
              },
            }}
            variant="primary"
            onClick={handleOnClick}
            loading={isPendingSetComplianceReport}>
            {t("common.complianceReport.bottomBar.button")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <TypographyContainer>{renderHeaderTextBlock()}</TypographyContainer>
      {renderContent()}
      {renderReviewBar()}
    </>
  );
};

export default ComplianceReportView;
